import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { LogOut, Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const AdminGroups = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: 'Orange',
    year: '2024',
    surface: '',
    description: '',
    project_ids: [],
    featured: false,
    homepage_position: null
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [groupsRes, projectsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/project-groups?limit=500`),
        axios.get(`${BACKEND_URL}/api/projects?limit=500`)
      ]);
      setGroups(groupsRes.data.groups || []);
      setProjects(projectsRes.data.projects || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setFormData({
      title: '',
      category: '',
      location: 'Orange',
      year: '2024',
      surface: '',
      description: '',
      project_ids: [],
      featured: false,
      homepage_position: null
    });
    setIsDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setFormData({
      title: group.title,
      category: group.category,
      location: group.location,
      year: group.year,
      surface: group.surface || '',
      description: group.description || '',
      project_ids: group.project_ids || [],
      featured: group.featured,
      homepage_position: group.homepage_position
    });
    setIsDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (editingGroup) {
        await axios.put(`${BACKEND_URL}/api/project-groups/${editingGroup.id}`, formData);
      } else {
        await axios.post(`${BACKEND_URL}/api/project-groups`, formData);
      }
      await fetchData();
      setIsDialogOpen(false);
      alert('Groupe sauvegardé avec succès!');
    } catch (error) {
      console.error('Error saving group:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe ?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/project-groups/${groupId}`);
      await fetchData();
      alert('Groupe supprimé avec succès!');
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const toggleProjectInGroup = (projectId) => {
    setFormData(prev => ({
      ...prev,
      project_ids: prev.project_ids.includes(projectId)
        ? prev.project_ids.filter(id => id !== projectId)
        : [...prev.project_ids, projectId]
    }));
  };

  const categories = ['Béton Imprimé', 'Béton Désactivé', 'Béton Bâlé'];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/gallery">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour Galerie
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Groupes de Projets</h1>
              <p className="text-slate-300 text-sm">{groups.length} groupes</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                🏠 Dashboard
              </Button>
            </Link>
            <Link to="/admin/crm">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                📊 CRM
              </Button>
            </Link>
            <Link to="/admin/gallery">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                📸 Galerie
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6">
        <div className="mb-6">
          <Button onClick={handleCreateGroup} size="lg" className="bg-slate-900">
            <Plus className="w-5 h-5 mr-2" />
            Créer un Groupe
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-xl text-slate-600">Chargement...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group) => (
              <Card key={group.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{group.title}</CardTitle>
                      <p className="text-sm text-slate-600">{group.category}</p>
                    </div>
                    {group.featured && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Featured</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm"><strong>Localisation:</strong> {group.location}</p>
                    <p className="text-sm"><strong>Images:</strong> {group.project_ids?.length || 0}</p>
                    {group.homepage_position && (
                      <p className="text-sm"><strong>Position homepage:</strong> {group.homepage_position}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={() => handleEditGroup(group)}>
                      <Edit className="w-3 h-3 mr-1" />
                      Modifier
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteGroup(group.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Modifier' : 'Créer'} un Groupe</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Ex: Piscine Orange"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Localisation</Label>
                  <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Année</Label>
                  <Input value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Surface</Label>
                  <Input value={formData.surface} onChange={(e) => setFormData({...formData, surface: e.target.value})} placeholder="100m²" />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                  />
                  <Label>Afficher sur homepage</Label>
                </div>
                {formData.featured && (
                  <div className="space-y-2">
                    <Label>Position (1-6)</Label>
                    <Input 
                      type="number" 
                      min="1" 
                      max="6"
                      value={formData.homepage_position || ''}
                      onChange={(e) => setFormData({...formData, homepage_position: parseInt(e.target.value) || null})}
                      placeholder="1"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Sélectionner les projets ({formData.project_ids.length} sélectionnés)</Label>
                <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {projects.map((project) => (
                      <div 
                        key={project.id}
                        className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${
                          formData.project_ids.includes(project.id) ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'
                        }`}
                        onClick={() => toggleProjectInGroup(project.id)}
                      >
                        <img src={project.image} alt={project.title} className="w-full h-24 object-cover" />
                        <div className="p-2 bg-white">
                          <p className="text-xs font-medium line-clamp-1">{project.title}</p>
                          <p className="text-xs text-slate-500">{project.category}</p>
                        </div>
                        {formData.project_ids.includes(project.id) && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">✓</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveGroup} className="w-full" disabled={!formData.title || !formData.category}>
                {editingGroup ? 'Mettre à jour' : 'Créer'} le Groupe
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminGroups;